<script setup lang="ts">
import ValueDisplay from '@/components/ValueDisplay.vue'
import ProjectsController from '@controllers/Banks/ProjectsController'
import { router } from '@inertiajs/vue3'
import Button from 'primevue/button'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ProjectSuggestionData = App.Data.Bank.ProjectSuggestionData

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    project: ProjectSuggestionData
    inNegotiations?: boolean
    actions?: {
      details?: boolean
      engage?: boolean
    }
  }>(),
  {
    inNegotiations: false,
  },
)

const previewImage = computed(() =>
  props.project.attachments?.find((attachment: App.Data.AttachmentData) => attachment.type === 'photo.project_preview'),
)

const uniqueValues = <T,>(arr: T[], key: keyof T) => [...new Set(arr?.map((item) => item[key]))].join(', ')

function updateProjectSuggestion(projectSuggestionId: string) {
  router.put(ProjectsController.update(projectSuggestionId), {
    status: 'proposed',
  })
}

function projectDetailsUrl(projectId: string) {
  return (props.inNegotiations ? ProjectsController.showNegotiation : ProjectsController.showRequest)(projectId).url
}
</script>

<template>
  <div class="flex flex-row gap-6">
    <div class="min-w-48">
      <img v-if="previewImage" :src="previewImage.path" class="h-full w-full object-cover" alt="Preview" />
      <div v-else class="bg-surface-200 text-surface-500 flex h-full w-full items-center justify-center rounded">
        <i class="pi pi-image" />
      </div>
    </div>

    <div class="grow">
      <div class="grid flex-1 grid-rows-3 gap-6 text-sm">
        <div class="grid grid-cols-4 gap-x-4">
          <ValueDisplay :label="t('columns.buying_price')" format="currency" :value="project.project.purchase_price" />
          <ValueDisplay :label="t('columns.city')" :value="uniqueValues(project.estate_objects, 'city')" />
          <ValueDisplay
            :label="t('columns.customer_type')"
            :value="uniqueValues(project.estate_objects, 'owner_type')" />
          <ValueDisplay
            :label="t('columns.project_type')"
            :lines="1"
            :value="uniqueValues(project.estate_objects, 'estate_object_type')" />
        </div>

        <div class="grid grid-cols-4 gap-x-4">
          <ValueDisplay
            :label="t('columns.customer_equity')"
            format="currency"
            :value="project.project.equity_contribution" />
          <ValueDisplay :label="t('columns.dsti')" format="percent" :value="project.kpi_dsti" />
          <ValueDisplay :label="t('columns.ltv')" format="percent" :value="project.kpi_ltv" />
          <ValueDisplay :label="t('columns.lvtv')" format="percent" :value="project.kpi_lvtv" />
        </div>

        <div class="grid grid-cols-4 gap-x-4">
          <ValueDisplay :label="t('columns.project_volume')" format="currency" :value="project.project.invest_volume" />
          <ValueDisplay :label="t('columns.dti')" format="percent" :value="project.kpi_dti" />
          <ValueDisplay :label="t('columns.kdf')" format="currency" :value="project.kpi_kdf" />
        </div>
      </div>
    </div>

    <div class="flex h-full gap-2">
      <Link v-if="actions?.details" :href="projectDetailsUrl(project.id)">
        <Button :label="t('actions.details')" severity="secondary" />
      </Link>
      <Button
        v-if="actions?.engage && project?.status === 'created'"
        :label="t('actions.propose')"
        severity="primary"
        @click="updateProjectSuggestion(project.id)" />
    </div>
  </div>
</template>

<i18n>
{
    "en": {
        "columns": {
            "preview": "Preview",
            "status": "Status",
            "city": "City",
            "buying_price": "Buying Price",
            "project_type": "Project Type",
            "project_volume": "Project Volume",
            "customer_type": "Customer Type",
            "customer_equity": "Equity",
            "actions": "Actions",
            "dsti": "Debt Service-to-Income (DSTI)",
            "dti": "Dept-to-Income (DTI)",
            "ltv": "Loan-to-Value (LTV)",
            "lvtv": "Loan-Volume-to-Value (LVTV)",
            "kdf": "Capital Servicing Capabilities"
        },
        "actions": {
            "details": "Details",
            "propose": "Propose"
        },
        "empty": "No project suggestions available."
    },
    "de": {
        "columns": {
            "preview": "Vorschau",
            "status": "Status",
            "city": "Stadt",
            "buying_price": "Kaufpreis",
            "project_type": "Projektart",
            "project_volume": "Projektvolumen",
            "customer_type": "Kundenart",
            "customer_equity": "Eigenkapital",
            "actions": "Aktionen",
            "dsti": "Kapitaldienstquote (DSTI)",
            "dti": "Verschuldungsquote (DTI)",
            "ltv": "Beleihungsauslauf (LTV)",
            "lvtv": "Darlehensvol. zum Wert (LVTV)",
            "kdf": "Kapitaldienstfähigkeit (KDF)"
        },
        "actions": {
            "details": "Details",
            "propose": "Vorschlagen"
        },
        "empty": "Keine Projektvorschläge verfügbar."
    }
}
</i18n>
